import { cn } from '../cn.js';

export interface BarListItem {
  label: string;
  value: number;
  /** Optional right-aligned annotation, e.g. "12 clicks". */
  meta?: string;
}

export interface BarListProps {
  items: BarListItem[];
  emptyLabel?: string;
  className?: string;
}

/**
 * Single-series horizontal magnitude bars: thin, rounded data-ends anchored to
 * the left baseline, one brand hue, value labelled directly. Bars are scaled to
 * the largest value in the set.
 */
export function BarList({ items, emptyLabel = 'No data yet.', className }: BarListProps) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className={cn('space-y-2', className)}>
      {items.map((item) => (
        <div key={item.label} className="space-y-1">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate">{item.label}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {item.meta ?? item.value}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${Math.max((item.value / max) * 100, 2)}%` }}
              role="img"
              aria-label={`${item.label}: ${item.value}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
